/**
 * Footage pulled off a camera's SD card, or out of the recorder archive.
 *
 * ── What this screen was waiting for, and was not ────────────────────────
 *
 * 🔴 It carried the blocker "waiting on the edge pull-job queue — retries,
 * partial results, a share-token authz surface and a 4G cost dimension across
 * two codebases, unscoped rather than merely unstarted". Every one of those
 * exists and has existed: `cosmon-recorder/recorder.js` has `edge_jobs` with
 * attempts and `next_retry_at`, `parts_json` for resumable partial pulls,
 * `edge_shares` with create/list/revoke, and separate concurrency caps for
 * cellular versus archive pulls. It has completed real work — 15 GB across
 * nine jobs, one of them a 2.09 GB three-hour pull.
 *
 * What was missing was this screen and the tenant scoping in front of it. The
 * queue was never the blocker; nothing had connected it to anything.
 *
 * ── Read-only, on purpose ────────────────────────────────────────────────
 *
 * Nothing here starts a pull. A pull moves gigabytes over a customer's 4G link,
 * so the button that spends that wants a cap, a size estimate and a
 * confirmation naming the cost — which is a decision, not an oversight. This
 * shows what exists, hands over the file, and manages who else can see it.
 *
 * ── A share link is a public URL ─────────────────────────────────────────
 *
 * ⚠️ Said in those words on the screen rather than called "Share", because the
 * recorder serves `/share/:token/file` to anyone holding the token, with no
 * sign-in. An operator sending a link to a customer should know that is what
 * they are doing; an operator who thinks it is access-controlled will send it
 * somewhere they should not.
 */

import { Download, Link } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Loading,
  Pill,
  Section,
  Toast,
  type ColumnDef,
  type PillState,
} from './components/index.ts';

interface Pull {
  id: string;
  assetId: string;
  assetName: string;
  assetCode: string | null;
  siteName: string | null;
  start_iso: string;
  end_iso: string;
  status: string;
  requested_by: string | null;
  bytes_written: number | null;
  duration_s: number | null;
  error: string | null;
  attempt: number;
  max_attempts: number;
  created_at: number;
  completed_at: number | null;
  source: string;
}

interface Recorder {
  ok: boolean;
  reason: string | null;
}

interface Share {
  token: string;
  created_at?: number;
  expires_at?: number | null;
}

function bytes(n: number | null): string {
  if (n === null || n === 0) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} MB`;
  return `${(n / 1e3).toFixed(0)} kB`;
}

function duration(s: number | null): string {
  if (s === null || s <= 0) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * The status, as a pill.
 *
 * `failed` and `queued` are deliberately not the same colour as `done`: the
 * whole point of showing the queue is that a pull somebody is waiting for can
 * be stuck, and a uniform list hides exactly that.
 */
function statusState(status: string): PillState {
  return status === 'done' ? 'ok' : status === 'failed' ? 'fault' : 'warning';
}

function StatusPill({ pull }: { pull: Pull }): React.JSX.Element {
  const retries = pull.attempt > 1 ? ` · attempt ${pull.attempt} of ${pull.max_attempts}` : '';
  return (
    <Pill state={statusState(pull.status)} reason={pull.error}>
      {pull.status}
      {retries}
    </Pill>
  );
}

const SHARE_COLUMNS: ColumnDef<Share, unknown>[] = [
  {
    id: 'token',
    header: 'Token',
    accessorKey: 'token',
    enableSorting: false,
    cell: ({ getValue }) => <code>{getValue() as string}</code>,
  },
  {
    id: 'created',
    header: 'Created',
    accessorFn: (s) => s.created_at ?? 0,
    cell: ({ getValue }) => {
      const at = getValue() as number;
      return <span className="small muted">{at > 0 ? new Date(at).toLocaleString() : '—'}</span>;
    },
  },
];

export function PullsView(): React.JSX.Element {
  const [pulls, setPulls] = useState<Pull[] | null>(null);
  const [recorder, setRecorder] = useState<Recorder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [shares, setShares] = useState<Share[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ jobs: Pull[]; recorder: Recorder }>('/api/pulls');
      setPulls(result.jobs);
      setRecorder(result.recorder);
      setError(null);
    } catch (err) {
      /*
       * 🔴 The error is shown, never swallowed into an empty list. A recorder
       * that cannot be reached and a tenant that has never pulled anything look
       * identical as an empty table, and only one of them means "nothing to
       * see here".
       */
      setError(err instanceof ApiError ? err.message : String(err));
      setPulls(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openShares = useCallback(async (id: string) => {
    setOpenId(id);
    setShares(null);
    try {
      const result = await api.get<{ shares: Share[] }>(`/api/pulls/${id}/shares`);
      setShares(result.shares);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  async function createShare(id: string): Promise<void> {
    setBusy(true);
    try {
      await api.post(`/api/pulls/${id}/share`, {});
      await openShares(id);
      setToast('Link created. Anyone who has it can download this footage without signing in.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string, token: string): Promise<void> {
    setBusy(true);
    try {
      await api.del(`/api/pulls/${id}/shares/${token}`);
      await openShares(id);
      setToast('Link revoked.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<ColumnDef<Pull, unknown>[]>(
    () => [
      {
        id: 'camera',
        header: 'Camera',
        accessorFn: (p) => p.assetCode ?? p.assetName,
        cell: ({ row }) => (
          <>
            {row.original.assetCode ?? row.original.assetName}
            {row.original.siteName === null ? null : (
              <span className="muted"> · {row.original.siteName}</span>
            )}
          </>
        ),
      },
      {
        id: 'covering',
        header: 'Covering',
        accessorKey: 'start_iso',
        cell: ({ row }) => (
          <>
            <code>{row.original.start_iso}</code>
            <span className="muted"> to </span>
            <code>{row.original.end_iso.slice(11)}</code>
            {row.original.source === 'recorder_archive' && (
              <span className="muted small"> · from archive</span>
            )}
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <StatusPill pull={row.original} />,
      },
      {
        id: 'size',
        header: 'Size',
        accessorFn: (p) => p.bytes_written ?? 0,
        meta: { align: 'right' },
        cell: ({ row }) => bytes(row.original.bytes_written),
      },
      {
        id: 'length',
        header: 'Length',
        accessorFn: (p) => p.duration_s ?? 0,
        meta: { align: 'right' },
        cell: ({ row }) => duration(row.original.duration_s),
      },
      {
        id: 'file',
        header: 'File',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'done' ? (
            <div className="row">
              <a className="btn btn-secondary btn-sm" href={`/api/pulls/${row.original.id}/file`} download>
                <span className="btn-icon-slot">
                  <Download size={14} />
                </span>
                <span>Download</span>
              </a>
              <Button
                size="sm"
                variant="ghost"
                icon={<Link size={14} />}
                onClick={() => void openShares(row.original.id)}
              >
                Links
              </Button>
            </div>
          ) : (
            <span className="muted">{row.original.error ?? '—'}</span>
          ),
      },
    ],
    [openShares],
  );

  const shareColumns = useMemo<ColumnDef<Share, unknown>[]>(
    () => [
      ...SHARE_COLUMNS,
      {
        id: 'revoke',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="danger"
            busy={busy}
            onClick={() => {
              if (openId !== null) void revoke(openId, row.original.token);
            }}
          >
            Revoke
          </Button>
        ),
      },
    ],
    // `revoke` is recreated per render but closes over nothing that changes,
    // so the memo keys on the two values the cell actually reads.
    [busy, openId],
  );

  if (error !== null && pulls === null) return <Banner tone="fault">{error}</Banner>;
  if (pulls === null) return <Loading rows={4} cols={6} label="Loading pulled footage" />;

  const open = openId === null ? null : (pulls.find((p) => p.id === openId) ?? null);

  return (
    <div className="stack">
      {error !== null && <Banner tone="fault">{error}</Banner>}

      {recorder !== null && !recorder.ok && (
        /*
         * 🔴 Said before the table, not instead of it. An empty list under a
         * silent header is the one reading this must never allow: "nothing
         * has been pulled" and "nobody could ask" are different facts and
         * only one of them is a clean bill of health.
         */
        <Banner tone="fault" title="The recorder could not be asked">
          {recorder.reason}
        </Banner>
      )}

      {recorder !== null && !recorder.ok ? null : (
        <Section
          flush
          title="Pull jobs"
          summary={`${pulls.length} for this customer · retrieved off the camera or out of the recorder archive. Pulls are requested by the operator who needs them; this screen shows what exists.`}
        >
          <DataTable
            data={pulls}
            columns={columns}
            getRowId={(p) => p.id}
            initialSort={[{ id: 'covering', desc: true }]}
            rowClass={(p) => (p.status === 'failed' ? 'row-fault' : undefined)}
            caption="Pulled footage"
            empty={<Empty title="No footage has been pulled for this customer" />}
          />
        </Section>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpenId(null)}
        title="Share links"
        {...(open !== null
          ? { subtitle: `${open.assetCode ?? open.assetName} · ${open.start_iso} to ${open.end_iso.slice(11)}` }
          : {})}
      >
        <div className="stack">
          <Banner tone="warn" title="A share link downloads this footage without signing in">
            Anyone who has the link has the video. Revoke one the moment it is no longer needed.
          </Banner>

          {shares === null ? (
            <Loading rows={2} cols={2} label="Loading links" />
          ) : (
            <Section flush>
              <DataTable
                data={shares}
                columns={shareColumns}
                getRowId={(s) => s.token}
                caption="Share links for this pull"
                empty={
                  <Empty
                    tone="good"
                    title="No links"
                    body="Nobody outside this customer can reach this file."
                  />
                }
              />
            </Section>
          )}

          <div className="row">
            <Button
              variant="primary"
              busy={busy}
              onClick={() => {
                if (openId !== null) void createShare(openId);
              }}
            >
              Create a public link
            </Button>
          </div>
        </div>
      </Drawer>

      {toast !== null && <Toast message={toast} tone="ok" onDismiss={() => setToast(null)} />}
    </div>
  );
}
